import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Image, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';
import ImageUpload from '../components/ImageUpload';

const Banners: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  // 新增/编辑在途锁（Modal 的 onOk 不感知 promise）+ 行内启停在途的行 ID
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/banner', { params: { page: p, pageSize: 10 } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      if (editItem) {
        await request.put(`/banner/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/banner', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  // 行内启停：一次只允许一处在途，其余按钮置灰，避免连点把状态翻回原值
  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/banner/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      onOk: async () => {
        await request.delete(`/banner/${id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '图片', dataIndex: 'image', width: 160, render: (v: string) => v ? <Image src={v} width={120} /> : '-' },
    { title: '跳转链接', dataIndex: 'link', render: (v: string) => v || '-' },
    { title: '排序', dataIndex: 'sort', width: 80 },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? <Tag color="success">启用</Tag> : <Tag>禁用</Tag> },
    {
      title: '操作', width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => { setEditItem(record); form.setFieldsValue(record); setModalOpen(true); }}>编辑</Button>
          <Button
            size="small"
            loading={busyId === record.id}
            disabled={busyId !== null && busyId !== record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>轮播图管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditItem(null); form.resetFields(); setModalOpen(true); }}>
          新增轮播图
        </Button>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />
      <Modal title={editItem ? '编辑轮播图' : '新增轮播图'} open={modalOpen}
        onOk={handleSubmit} onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}>
        <Form form={form} layout="vertical">
          <Form.Item name="image" label="图片" rules={[{ required: true, message: '请上传轮播图' }]}>
            <ImageUpload tip="建议尺寸 2.3:1（如 1000×430），小程序首页通栏展示，超出部分会被裁切" />
          </Form.Item>
          <Form.Item name="link" label="跳转页面（可选）">
            <Input placeholder="如 /pages/products/index" />
          </Form.Item>
          <Form.Item name="sort" label="排序" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Banners;
